import mapKeys from 'lodash/mapKeys';
import snakeCase from 'lodash/snakeCase';
import { getDeviceType } from '@tencent/oneid-utils';
import { APP_TYPE, INVITATION_NOT_FOUND, VERIFY_CODE_SCENE } from 'common/constants/Constants';
import { INVITATION_STATUS, IResponseType, TInvitationInfo } from 'common/constants/Types';
import { IAppsConfig } from 'common/http/bff/Apps';
import i18n from 'common/locales';
import Utils from 'common/utils';
import JSRuntime from 'common/utils/JSRuntime';
import { LOGIN_VERIFY_TYPE, SUBMIT_RESULT } from 'idpBase/Constants';
import { AuthApi, AuthnApi } from 'idpBase/http';
import { APPROVAL_RESULT_TYPES, IRedirectInfo } from 'idpBase/http/services/AuthApi';
import { TInvitationAccount } from 'idpBase/http/type';
import { TGlobalStore, TUpdateStore } from 'idpBase/store';
import { createCaptchaReport } from 'idpBase/utils';
import {
  AUTH_INVALID_ERROR_CODE,
  CONTENT,
  EMAIL_SET_PWD_ERROR_CODE,
  EXPIRE_CODES,
  INVITE_FROM,
  RESULT_MAP,
  RESULT_TYPES,
  REVERIFY_CODE_ERROR,
  VERIFY_CODE_ERROR,
  WRONG_CODE_ERROR_CODE_LIST,
} from './constants/Constants';
import { IInvitationInfo } from './constants/Type';
import showToast from './utils/show-toast';

class InvitationStore {
  public readonly global: TGlobalStore;
  public updateStore: TUpdateStore<InvitationStore>;
  public resetStore: () => void;

  public currentContent: CONTENT;
  public isCheckLoading = true;
  public handshakeId = '';
  public accountId = '';
  public appInfo: IAppsConfig;

  public invitationInfo = {} as IInvitationInfo;
  public inactiveErr: IResponseType;
  public dialogVisible = false;
  public verificationSessionId = '';
  public countdown = 0;
  public isJoinLoading = false; // 真正加入的loading
  public resultType = RESULT_TYPES.INIT;
  public inviteFrom = '';
  public onlyApp = '';
  public redirectInfo: IRedirectInfo; // 加入成功后结果页信息

  public deptInvitationInfo: TInvitationInfo;
  public deptInvitationUser: string;
  public deptInvitationAccount: TInvitationAccount;
  public deptInvitationId: string;
  public deptInvitationCode: string;

  /** 用于传给后端的app字段，查询为toa时，转为undefined */
  public get appType() {
    return this.appInfo?.app_type !== APP_TYPE.ONEID ? this.appInfo?.app_type : undefined;
  }

  // 获取邀请信息：过期，人员信息等，也当作过期校验
  public async getInvitationInfo() {
    const res = await AuthnApi.getInvitationInfo(this.accountId, this.handshakeId);
    if (res.errCode) {
      const isExpired = EXPIRE_CODES.includes(res.errCode);
      this.updateStore({
        isCheckLoading: false,
        currentContent: CONTENT.RESULT_PAGE,
        inactiveErr: !isExpired ? res : undefined,
        resultType: RESULT_TYPES.EXPIRED,
      });
      return res;
    }
    this.updateStore({
      invitationInfo: res as IInvitationInfo,
      appInfo: mapKeys(res.app, (_value, key) => snakeCase(key)),
      onlyApp: res.onlyApp,
      isCheckLoading: false,
    });
    if (res.handshakeStatus === RESULT_TYPES.JOIN_SUCCESS) {
      this.updateStore({
        currentContent: CONTENT.RESULT_PAGE,
        resultType: RESULT_TYPES.JOIN_SUCCESS,
        redirectInfo: mapKeys(res.redirectInfo, (_value, key) =>
          snakeCase(key),
        ) as unknown as IRedirectInfo,
      });
    } else if ([RESULT_TYPES.EXPIRED, RESULT_TYPES.REFUSED].includes(res.handshakeStatus)) {
      this.updateStore({
        currentContent: CONTENT.RESULT_PAGE,
        resultType: res.handshakeStatus,
      });
    } else {
      this.updateStore({
        currentContent: CONTENT.INVITING,
      });
    }
    return res;
  }

  // 接受邀请
  public async acceptInvitation() {
    const isEmail = this.inviteFrom === INVITE_FROM.EMAIL;
    const handshakeId = m.get(this.invitationInfo, 'handshakeId');
    const mobile = m.get(this.invitationInfo, 'mobile');
    const email = m.get(this.invitationInfo, 'email');

    const res = await (isEmail
      ? AuthnApi.acceptEmailInvitation(handshakeId, email)
      : AuthnApi.acceptInvitation([{ mobile, handshakeId }], JSRuntime.appFromUrl));

    const reportData = {
      app_name: this.invitationInfo.appLogoType,
      handshake_id: this.handshakeId,
      account_uid: this.accountId,
      account_name: this.invitationInfo.account?.companyInfo?.companyName,
      device_type: getDeviceType(),
      joinnow_result: 0,
    };

    if (res.errCode) {
      this.global.rootStore.dataReport('e#oneid_joinPhone_page#all#click', {
        ...reportData,
        error_message: res.errMessage,
      });

      if (res.errCode === EMAIL_SET_PWD_ERROR_CODE) {
        this.updateStore({ dialogVisible: true });
      } else if (EXPIRE_CODES.includes(res.errCode)) {
        // 邀请链接过期
        this.updateStore({
          currentContent: CONTENT.RESULT_PAGE,
          resultType: RESULT_TYPES.EXPIRED,
        });
      } else {
        showToast({ message: res.errMessage ?? '' });
      }
      return false;
    }

    this.global.rootStore.dataReport('e#oneid_joinPhone_page#all#click', reportData);
    // 未加入时获取的信息不全，加入成功后需要重新调用详情获取
    await this.getInvitationInfo();
    return true;
  }

  public async getVerifyCode(mobile: string, resultReport?: string) {
    // 避免外侧验证码组件不重新计时
    this.countdown = 0;
    try {
      const reportParams = createCaptchaReport(
        this.global.rootStore.getReportGlobalParams(),
        LOGIN_VERIFY_TYPE.MOBILE_HANDSHAKE_JOIN,
      );
      const res = await AuthnApi.sendVerificationCode(
        { mobile, event: VERIFY_CODE_SCENE.INVITATION },
        reportParams,
      );
      resultReport &&
        this.global.rootStore.dataReport(resultReport, {
          submit_result: res.errCode ? SUBMIT_RESULT.FAIL : SUBMIT_RESULT.SUCCESS,
        });
      if (res?.errCode) return;
      this.updateStore({
        currentContent: CONTENT.MOBILE_VERIFY,
        countdown: m.get(res, 'expire') || 0,
        verificationSessionId: m.get(res, 'verificationSessionId'),
      });
    } catch (err) {
      resultReport &&
        this.global.rootStore.dataReport(resultReport, {
          submit_result: SUBMIT_RESULT.FAIL,
          isSendBeacon: true,
        });
      // 验证码取消场景，需要兼容组件需要捕获异常的场景
      return err;
    }
  }

  public async verifyCode(code: string) {
    const res = await AuthnApi.verifyCode({
      verificationSessionId: this.verificationSessionId,
      verificationCode: code,
      event: VERIFY_CODE_SCENE.INVITATION,
    });
    if (res.errCode) {
      const verifyErrorMessage = VERIFY_CODE_ERROR[res.errCode];
      showToast({
        message: verifyErrorMessage
          ? i18n.t(verifyErrorMessage, { ns: 'idp' })
          : res.errMessage || '',
      });
    }
    return res;
  }

  public async setEmailPassword(password: string) {
    // TODO: 临时，待后端问题查出后删除
    const email = m.get(this.invitationInfo, 'email');

    const res = await AuthnApi.setEmailPassword({
      password,
      email,
      event: VERIFY_CODE_SCENE.EMAIL_HANDSHAKE,
    });

    if (res.errCode) {
      if (EXPIRE_CODES.includes(res.errCode)) {
        this.updateStore({
          currentContent: CONTENT.RESULT_PAGE,
          resultType: RESULT_TYPES.EXPIRED,
        });
      }
    }
  }

  // 拒绝加入
  public async declineInvitation() {
    const handshakeId = m.get(this.invitationInfo, 'handshakeId');
    const reportData = {
      app_name: this.invitationInfo.appLogoType,
      handshake_id: this.handshakeId,
      account_uid: this.accountId,
      account_name: this.invitationInfo.account?.companyInfo?.companyName,
      device_type: getDeviceType(),
      joinnow_result: 1,
    };
    const res = await AuthnApi.declineInvitation([handshakeId]);
    if (res.errCode) {
      const errMessage = m.get(res, 'data.errMessage');
      this.global.rootStore.dataReport('e#oneid_joinPhone_page#all#click', {
        ...reportData,
        error_message: errMessage,
      });
      return;
    }
    this.global.rootStore.dataReport('e#oneid_joinPhone_page#all#click', reportData);
    this.updateStore({
      currentContent: CONTENT.RESULT_PAGE,
      resultType: RESULT_TYPES.REFUSED,
    });
  }

  public async getDeptInvitation(
    params: { invitation_id?: string; inv_code?: string } = {
      invitation_id: this.deptInvitationId,
      inv_code: this.deptInvitationCode,
    },
    needWrongCodeRes?: boolean,
    keepInput?: boolean,
  ) {
    const curApp = JSRuntime.appFromUrl === APP_TYPE.ONEID ? undefined : JSRuntime.appFromUrl;
    const invitationRet = await AuthApi.getDeptInvitation({ ...params, app: curApp });
    if (invitationRet.errCode) {
      // 失效
      if (INVITATION_NOT_FOUND.includes(invitationRet.errCode)) {
        this.updateStore({
          resultType: RESULT_TYPES.EXPIRED,
          isCheckLoading: false,
          currentContent: CONTENT.RESULT_PAGE,
        });
        return;
      }
      // 验证码错误，需要输入框提示
      if (WRONG_CODE_ERROR_CODE_LIST.includes(invitationRet.errCode) && needWrongCodeRes)
        return invitationRet;
      showToast({ message: invitationRet.errMessage || '' });
      return invitationRet;
    }
    const { invitation, account, account_user, app, redirect_info } = invitationRet;
    const approvalStatus = redirect_info?.approval_status || APPROVAL_RESULT_TYPES.INITIALIZED;
    // 失效
    const isExpire = invitation.inv.status === INVITATION_STATUS.DISABLED;
    this.updateStore({
      currentContent:
        !isExpire && (approvalStatus === APPROVAL_RESULT_TYPES.INITIALIZED || keepInput)
          ? CONTENT.DEPT_INVITATION
          : CONTENT.RESULT_PAGE,
      resultType: isExpire ? RESULT_TYPES.EXPIRED : RESULT_MAP[approvalStatus],
      redirectInfo: redirect_info,
      isCheckLoading: false,
      deptInvitationInfo: invitation,
      deptInvitationUser: account_user.name,
      deptInvitationAccount: account,
      appInfo: app,
    });
    return invitationRet;
  }

  public async joinDeptInvitation(verification_code: string, user_name: string) {
    const joinRet = await AuthApi.sendJoinRequest({
      invitation_id: this.deptInvitationId,
      invitation_code: this.deptInvitationCode,
      verification_code,
      state_token: this.verificationSessionId,
      user_name,
      // 默认邀请逻辑不需要传入app参数
      app: this.appType,
    });
    if (joinRet.errCode) return joinRet;
    let resultType = RESULT_TYPES.FAIL;
    if (joinRet.invitation_status === 0) {
      resultType = RESULT_TYPES.JOIN_SUCCESS;
    }
    if (joinRet.invitation_status === 1) {
      // 如果用户已完成注册，则直接跳转成功页面
      resultType = RESULT_MAP[joinRet.approval_status ?? APPROVAL_RESULT_TYPES.PENDING];
    }
    this.updateStore({
      currentContent: CONTENT.RESULT_PAGE,
      resultType,
      redirectInfo: joinRet,
    });
    return joinRet;
  }

  public async smsCodeSend(mobile: string) {
    const reportParams = createCaptchaReport(
      this.global.rootStore.getReportGlobalParams(),
      LOGIN_VERIFY_TYPE.MOBILE_HANDSHAKE_JOIN,
    );
    const sendCodeRet = await AuthApi.smsCodeSend({ mobile }, reportParams);
    if (sendCodeRet.errCode) return sendCodeRet;
    this.updateStore({ verificationSessionId: sendCodeRet.state_token });
    return sendCodeRet;
  }

  /** 验证成功后 跳转链接 */
  public async redirectAuth() {
    let res: IResponseType;
    if (this.handshakeId) {
      res = await AuthnApi.handshakeTakeEffect();
    } else {
      res = await AuthApi.invitationTakeEffect({ app: this.appType });
    }
    if (res.errCode) {
      // 验证码失效的场景，需要弹出手机号校验框重新授权
      if (res.errCode === AUTH_INVALID_ERROR_CODE) {
        this.updateStore({ currentContent: CONTENT.MOBILE_REVERIFY });
      }
      // 特殊场景需要前端处理报错
      showToast({
        message:
          i18n.t(REVERIFY_CODE_ERROR[res.errCode], {
            name:
              this.deptInvitationAccount?.name ||
              this.invitationInfo?.account?.companyInfo?.companyName,
          }) ||
          res.errMessage ||
          '',
      });
      return;
    }
    if (!res.redirect_url && !res.redirectUrl) return;
    Utils.safeRedirect(res.redirect_url || res.redirectUrl);
  }
}

export default InvitationStore;
